import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineBusIcon from 'LuxKit/Icons/line/LineBusIcon'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineMapPinIcon from 'LuxKit/Icons/line/LineMapPinIcon'
import LineNotesIcon from 'LuxKit/Icons/line/LineNotesIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineSubwayIcon from 'LuxKit/Icons/line/LineSubwayIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineUniversityIcon from 'LuxKit/Icons/line/LineUniversityIcon'
import type { SvgIconComponent } from 'LuxKit/SvgIcon'
import LeIcon from 'tripPlanner/components/Icons/LeIcon'
import type {
  ItemType,
  LETripItem,
  ParentItemType,
} from 'tripPlanner/types/tripItem'
import { defineMessages, type MessageDescriptor } from 'react-intl'

interface ParentItemViewModel {
  name: MessageDescriptor
  icon: SvgIconComponent
  description?: MessageDescriptor
}

interface ItemViewModel {
  name: MessageDescriptor
  singularName: MessageDescriptor
  icon: SvgIconComponent
  editable: boolean
  labels?: {
    place?: MessageDescriptor
    phone?: MessageDescriptor
    confirmation?: MessageDescriptor
    url?: MessageDescriptor
    notes?: MessageDescriptor
  }
}

const parentItemMessages = defineMessages({
  leBookingsName: {
    id: 'ATK9G-hReINRseeQ-zLo',
    defaultMessage: 'My Bookings',
    description: 'Parent item type name for Luxury Escapes bookings in trip planner',
  },
  placeName: {
    id: '7kxBqfUauTN27dsgL76Q',
    defaultMessage: 'Place',
    description: 'Parent item type name for places in trip planner in the trip planner',
  },
  placeDescription: {
    id: 'OQ0UrkTa_ZQ-3u0gWmPW',
    defaultMessage: 'Attractions, restaurants, activities and more',
    description: 'Description for the Place parent item type in trip planner',
  },
  flightName: {
    id: 'qZ3F6KCaVmdMS7F1FlBM',
    defaultMessage: 'Flight',
    description: 'Parent item type name for flights in trip planner in the trip planner',
  },
  accommodationName: {
    id: 'e54cf5dqvXxwWwz6q3hi',
    defaultMessage: 'Accommodation',
    description: 'Parent item type name for accommodation in trip planner',
  },
  tourAndCruisesName: {
    id: 'm4Zq3A17h_gR4wKkbX3Y',
    defaultMessage: 'Tours & Cruises',
    description: 'Parent item type name for tours and cruises in trip planner',
  },
  tourAndCruisesDescription: {
    id: 'Zi0empDUxRJf3wHxfdEQ',
    defaultMessage: 'Multi-day guided experiences',
    description: 'Description for Tours & Cruises parent item type in trip planner',
  },
  tourName: {
    id: 'OZVoGVqrUa7apE8H1Lao',
    defaultMessage: 'Tour',
    description: 'Parent item type name for tours in trip planner in the trip planner',
  },
  tourDescription: {
    id: 'dR1dAn4EDPvGbFD_rLCe',
    defaultMessage: 'Day or multi-day tours',
    description: 'Description for Tour parent item type in trip planner',
  },
  cruiseName: {
    id: 'QEjDJNshMyj9oWbOdbQY',
    defaultMessage: 'Cruise',
    description: 'Parent item type name for cruises in trip planner in the trip planner',
  },
  carRentalName: {
    id: 'MXqBD0kqN6JftrDLtMlh',
    defaultMessage: 'Car Rental',
    description: 'Parent item type name for car rental in trip planner',
  },
  carRentalDescription: {
    id: '07NI6ECXM_C15cMuqJMJ',
    defaultMessage: 'Renting private vehicles',
    description: 'Description for Car Rental parent item type in trip planner',
  },
  transportName: {
    id: 'HUMiHUZvCCrZNG7KmjWj',
    defaultMessage: 'Transport',
    description: 'Parent item type name for transport in trip planner',
  },
  transportDescription: {
    id: '5qiWarkfUbutIiB5lJ6n',
    defaultMessage: 'Car rental, rail, bus, ferry',
    description: 'Description for Transport parent item type in trip planner',
  },
  noteName: {
    id: 'g-JRdFiqVHgTz-EfAL3Q',
    defaultMessage: 'Note',
    description: 'Parent item type name for notes in trip planner in the trip planner',
  },
})

const labelMessages = defineMessages({
  additionalNotes: {
    id: 'YWF2AVEeg3rmZPU7UYRZ',
    defaultMessage: 'Additional notes',
    description: 'Label for additional notes field on trip planner item',
  },
  placeName: {
    id: '0ip8aJIfyYfSkHCN6XJv',
    defaultMessage: 'Place name',
    description: 'Label for place name field on accommodation trip item',
  },
  phoneNumber: {
    id: 'HC_eZCDxq1FAZNR2OfrQ',
    defaultMessage: 'Phone number',
    description: 'Form field label for phone number field on trip planner item',
  },
  confirmationCode: {
    id: 'prW-Mm6-lUiAopUzEwcN',
    defaultMessage: 'Confirmation code',
    description: 'Label for confirmation/booking code field on trip planner item',
  },
  name: {
    id: 'LTQ9Y0ILKPtUhI0XeUk8',
    defaultMessage: 'Name',
    description: 'Form field label for the name field on restaurant/bar trip item',
  },
  website: {
    id: 'ESwLUagxnXuqyhprcUSI',
    defaultMessage: 'Website',
    description: 'Form field label for website URL field on trip planner item',
  },
  reservationNumber: {
    id: 'xJJVflX7z2kQ7lGnqWj8',
    defaultMessage: 'Reservation number',
    description: 'Label for reservation number field on restaurant/bar trip item',
  },
})

const leProductMessages = defineMessages({
  cruise: {
    id: 'WuSKow8VRafAGu_NnKhp',
    defaultMessage: 'Cruise',
    description: 'Product type title for Luxury Escapes cruise booking in trip planner',
  },
  experience: {
    id: 'jHIDH5DLWrSAybMnFKHc',
    defaultMessage: 'Experience',
    description: 'Product type title for Luxury Escapes experience booking in trip planner',
  },
  flight: {
    id: 'mOx0PvKzQ_lhOMtX0cAy',
    defaultMessage: 'Flight',
    description: 'Product type title for Luxury Escapes flight booking in trip planner',
  },
  hotel: {
    id: 'sMzopUdeEg49WvTZ7mca',
    defaultMessage: 'Hotel',
    description: 'Product type title for Luxury Escapes hotel booking in trip planner',
  },
  tour: {
    id: 'Lv5u2ovr7PS0jrxiL8Tb',
    defaultMessage: 'Tour',
    description: 'Product type title for Luxury Escapes tour booking in trip planner',
  },
  villa: {
    id: 'sKqJxspAXcxPAaTqmHm0',
    defaultMessage: 'Villa',
    description: 'Product type title for Luxury Escapes villa/rental booking in trip planner',
  },
})

const itemMessages = defineMessages({
  accommodationName: {
    id: 'f01Q228W8KE5N85XfgTb',
    defaultMessage: 'Accommodation',
    description: 'Item type plural name for accommodation in trip planner',
  },
  accommodationSingular: {
    id: 'j0m43TI863yP04VvM6bB',
    defaultMessage: 'Accommodation',
    description: 'Item type singular name for accommodation in trip planner',
  },
  attractionsName: {
    id: 'o48KVnuquNSOIXnq80BA',
    defaultMessage: 'Attractions',
    description: 'Item type plural name for attractions in trip planner',
  },
  attractionSingular: {
    id: '2a9HhbKTNte8EhWfHNZB',
    defaultMessage: 'Attraction',
    description: 'Item type singular name for attraction in trip planner',
  },
  carRentalName: {
    id: 'e8qM6eTe9Cwh6UXlOXwR',
    defaultMessage: 'Car Rental',
    description: 'Item type plural name for car rental in trip planner',
  },
  carHireSingular: {
    id: '7Qe-vVjh1rqUIhXVKKBc',
    defaultMessage: 'Car Hire',
    description: 'Item type singular name for car hire in trip planner',
  },
  eventsName: {
    id: '1XNhqNb-79IAg8Y5vlOf',
    defaultMessage: 'Events',
    description: 'Item type plural name for events in trip planner in the trip planner',
  },
  eventSingular: {
    id: 've28Z6OdKn29JzFwhyTZ',
    defaultMessage: 'Event',
    description: 'Item type singular name for event in trip planner in the trip planner',
  },
  experiencesName: {
    id: 'o-wCC5vnTUbBCv9FrTBR',
    defaultMessage: 'Experiences',
    description: 'Item type plural name for experiences in trip planner',
  },
  experienceSingular: {
    id: '2b-HjejiAVv3xEBr_sOM',
    defaultMessage: 'Experience',
    description: 'Item type singular name for experience in trip planner',
  },
  flightsName: {
    id: 'sVBTfNB4SA4nZEC6d446',
    defaultMessage: 'Flights',
    description: 'Item type plural name for flights in trip planner in the trip planner',
  },
  flightSingular: {
    id: 'OPvwfuDGK4mO4L7P2AQN',
    defaultMessage: 'Flight',
    description: 'Item type singular name for flight in trip planner',
  },
  restaurantsAndBarsName: {
    id: 'oRRUQHmDwoh8pOhkYTj7',
    defaultMessage: 'Restaurants & Bars',
    description: 'Item type plural name for restaurants and bars in trip planner',
  },
  restaurantBarSingular: {
    id: '_DYoYmPahZv3dzsg6oVc',
    defaultMessage: 'Restaurant/Bar',
    description: 'Item type singular name for restaurant/bar in trip planner',
  },
  busesName: {
    id: '2BApbyR7g5TjroQ2JZPK',
    defaultMessage: 'Buses',
    description: 'Item type plural name for buses in trip planner in the trip planner',
  },
  busSingular: {
    id: 'HcRBHAB2Lgjc_RN8SjAP',
    defaultMessage: 'Bus',
    description: 'Item type singular name for bus in trip planner in the trip planner',
  },
  ferriesName: {
    id: '6kYS_hr2mS6ecJo8w5eA',
    defaultMessage: 'Ferries',
    description: 'Item type plural name for ferries in trip planner in the trip planner',
  },
  ferrySingular: {
    id: 'fHlrhY5c04Yn4P6xkauw',
    defaultMessage: 'Ferry',
    description: 'Item type singular name for ferry in trip planner in the trip planner',
  },
  privateTransportName: {
    id: 'lv7GevLHwx6vzp66GGxo',
    defaultMessage: 'Private Transport',
    description: 'Item type name for private transport in trip planner',
  },
  privateTransportSingular: {
    id: 'fPexxHkJK2ePotz50AYc',
    defaultMessage: 'Private Transport',
    description: 'Item type singular name for private transport in trip planner',
  },
  otherName: {
    id: '40MTqj-2JS9Ko3-LiVk9',
    defaultMessage: 'Other',
    description: 'Item type plural name for miscellaneous things to do in trip planner',
  },
  thingToDoSingular: {
    id: 'ik6rtgT_GS7sCz6nJR9v',
    defaultMessage: 'Thing to do',
    description: 'Item type singular name for miscellaneous activity in trip planner',
  },
  trainsName: {
    id: 'HzZs0oKBH-Lc9uAuui2w',
    defaultMessage: 'Trains',
    description: 'Item type plural name for trains in trip planner in the trip planner',
  },
  trainSingular: {
    id: '0eWJhvUwTSSQ7puloVZ1',
    defaultMessage: 'Train',
    description: 'Item type singular name for train in trip planner in the trip planner',
  },
  toursName: {
    id: 'FbkRmoWyGWeH59BhIA_Z',
    defaultMessage: 'Tours',
    description: 'Item type plural name for tours in trip planner in the trip planner',
  },
  tourSingular: {
    id: 'R2aCPyYfVaJoKwRohts8',
    defaultMessage: 'Tour',
    description: 'Item type singular name for tour in trip planner in the trip planner',
  },
  cruisesName: {
    id: 'sFUNGWA_62E9q4r7uK7T',
    defaultMessage: 'Cruises',
    description: 'Item type plural name for cruises in trip planner in the trip planner',
  },
  cruiseSingular: {
    id: 'faTg5hUV7L0_bqCPTQg6',
    defaultMessage: 'Cruise',
    description: 'Item type singular name for cruise in trip planner',
  },
  notesName: {
    id: 't7WYMsXJBsUCI4auOVu0',
    defaultMessage: 'Notes',
    description: 'Item type plural name for notes in trip planner in the trip planner',
  },
  noteSingular: {
    id: 'xXsORyXFwHQccFuykDRo',
    defaultMessage: 'Note',
    description: 'Item type singular name for note in trip planner in the trip planner',
  },
})

const filterMessages = defineMessages({
  stays: {
    id: 'yIyhFYv673m893pg8ePe',
    defaultMessage: 'Stays',
    description: 'Filter label for stays/accommodation in trip planner',
  },
  transport: {
    id: '2a7zooIgT9Jx_qLWVz1P',
    defaultMessage: 'Transport',
    description: 'Filter label for transport items in trip planner in the trip planner',
  },
  thingsToDo: {
    id: 'EHdcPHlNr5AsQkTvDy2q',
    defaultMessage: 'Things to do',
    description: 'Filter label for activities/things to do in trip planner',
  },
  restaurants: {
    id: 'uiDhHUXFBqbMe-wUOfMY',
    defaultMessage: 'Restaurants',
    description: 'Filter label for restaurants in trip planner in the trip planner',
  },
})

export const ParentItemMetadata: Record<ParentItemType, ParentItemViewModel> = {
  LE_BOOKINGS: {
    name: parentItemMessages.leBookingsName,
    icon: LeIcon,
  },
  THINGS_TO_DO: {
    name: parentItemMessages.placeName,
    icon: LineMapMarkerIcon,
    description: parentItemMessages.placeDescription,
  },
  FLIGHT: {
    name: parentItemMessages.flightName,
    icon: LinePlaneFlyIcon,
  },
  ACCOMMODATION: {
    name: parentItemMessages.accommodationName,
    icon: LineBedIcon,
  },
  TOUR_AND_CRUISES: {
    name: parentItemMessages.tourAndCruisesName,
    icon: LineCompassIcon,
    description: parentItemMessages.tourAndCruisesDescription,
  },
  TOURS: {
    name: parentItemMessages.tourName,
    icon: LineCompassIcon,
    description: parentItemMessages.tourDescription,
  },
  CRUISES: {
    name: parentItemMessages.cruiseName,
    icon: LineShipIcon,
  },
  CAR: {
    name: parentItemMessages.carRentalName,
    icon: LineCarIcon,
    description: parentItemMessages.carRentalDescription,
  },
  TRANSPORT: {
    name: parentItemMessages.transportName,
    icon: LineCarIcon,
    description: parentItemMessages.transportDescription,
  },
  NOTE: {
    name: parentItemMessages.noteName,
    icon: LineNotesIcon,
  },
}

const baseLabels: ItemViewModel['labels'] = {
  notes: labelMessages.additionalNotes,
}

export const LeItemProductTitle: Record<
  Exclude<LETripItem['sourceType'], 'NA' | 'Explore'>,
  MessageDescriptor
> = {
  LE_Cruise: leProductMessages.cruise,
  LE_Experience: leProductMessages.experience,
  LE_Flight: leProductMessages.flight,
  LE_Hotel: leProductMessages.hotel,
  LE_Hotel_Bedbank: leProductMessages.hotel,
  LE_Tour: leProductMessages.tour,
  LE_Accommodation_Rental: leProductMessages.villa,
}

export const ItemMetadata: Record<ItemType, ItemViewModel> = {
  ACCOMMODATION: {
    name: itemMessages.accommodationName,
    singularName: itemMessages.accommodationSingular,
    icon: LineBedIcon,
    labels: {
      ...baseLabels,
      place: labelMessages.placeName,
      phone: labelMessages.phoneNumber,
      confirmation: labelMessages.confirmationCode,
    },
    editable: true,
  },
  ATTRACTION: {
    name: itemMessages.attractionsName,
    singularName: itemMessages.attractionSingular,
    icon: LineUniversityIcon,
    editable: true,
  },
  CAR_RENTAL: {
    name: itemMessages.carRentalName,
    singularName: itemMessages.carHireSingular,
    icon: LineCarIcon,
    editable: true,
  },
  EVENT: {
    name: itemMessages.eventsName,
    singularName: itemMessages.eventSingular,
    icon: LineTicketIcon,
    editable: true,
  },
  EXPERIENCE: {
    name: itemMessages.experiencesName,
    singularName: itemMessages.experienceSingular,
    icon: LineTicketIcon,
    editable: false,
  },
  FLIGHT: {
    name: itemMessages.flightsName,
    singularName: itemMessages.flightSingular,
    icon: LinePlaneFlyIcon,
    labels: {
      ...baseLabels,
      confirmation: labelMessages.confirmationCode,
    },
    editable: true,
  },
  RESTAURANT_BAR: {
    name: itemMessages.restaurantsAndBarsName,
    singularName: itemMessages.restaurantBarSingular,
    icon: LineRestaurantIcon,
    labels: {
      ...baseLabels,
      place: labelMessages.name,
      url: labelMessages.website,
      confirmation: labelMessages.reservationNumber,
    },
    editable: true,
  },
  BUS: {
    name: itemMessages.busesName,
    singularName: itemMessages.busSingular,
    icon: LineBusIcon,
    editable: true,
  },
  FERRY: {
    name: itemMessages.ferriesName,
    singularName: itemMessages.ferrySingular,
    icon: LineShipIcon,
    editable: true,
  },
  OTHER_TRANSPORT: {
    name: itemMessages.privateTransportName,
    singularName: itemMessages.privateTransportSingular,
    icon: LineCarIcon,
    editable: true,
  },
  THING_TO_DO: {
    name: itemMessages.otherName,
    singularName: itemMessages.thingToDoSingular,
    icon: LineMapPinIcon,
    editable: true,
  },
  TRAIN: {
    name: itemMessages.trainsName,
    singularName: itemMessages.trainSingular,
    icon: LineSubwayIcon,
    editable: true,
  },
  TOUR: {
    name: itemMessages.toursName,
    singularName: itemMessages.tourSingular,
    icon: LineCompassIcon,
    editable: true,
  },
  CRUISE: {
    name: itemMessages.cruisesName,
    singularName: itemMessages.cruiseSingular,
    icon: LineShipIcon,
    editable: true,
  },
  NOTE: {
    name: itemMessages.notesName,
    singularName: itemMessages.noteSingular,
    icon: LineNotesIcon,
    editable: true,
  },
}

export const FilterIcons: Record<
  App.TripPlannerSubFilterType,
  SvgIconComponent
> = {
  Stay: ItemMetadata.ACCOMMODATION.icon,
  Tour: ItemMetadata.TOUR.icon,
  Flight: ItemMetadata.FLIGHT.icon,
  'Thing to do': ItemMetadata.EXPERIENCE.icon,
  Restaurant: ItemMetadata.RESTAURANT_BAR.icon,
  Transport: ItemMetadata.OTHER_TRANSPORT.icon,
  Cruise: ItemMetadata.CRUISE.icon,
  Note: ItemMetadata.NOTE.icon,
}

export const FilterLabels: Record<App.TripPlannerSubFilterType, MessageDescriptor> = {
  Stay: filterMessages.stays,
  Tour: ItemMetadata.TOUR.name,
  Flight: ItemMetadata.FLIGHT.name,
  Transport: filterMessages.transport,
  'Thing to do': filterMessages.thingsToDo,
  Restaurant: filterMessages.restaurants,
  Cruise: ItemMetadata.CRUISE.name,
  Note: ItemMetadata.NOTE.name,
}

/** Don't change the analytics values, even if we change the labels elsewhere */
export const FilterAnalyticsLabels: Record<
  App.TripPlannerSubFilterType,
  string
> = {
  Stay: 'stays',
  Tour: 'tours',
  Flight: 'flights',
  Transport: 'transport',
  'Thing to do': 'things_to_do',
  Restaurant: 'restaurants',
  Cruise: 'cruises',
  Note: 'notes',
}
